import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { LayoutProvider } from "./contexts/LayoutContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { ToastProvider, useToastContext } from "./contexts/ToastContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ToastContainer } from "./components/shared/Toast";
import ScrollToTop from "./components/shared/ScrollToTop";
// import { InternetStatus } from "./components/shared/InternetStatus";
// InternetStatus
// Layout Components
import ClientLayout from "./components/shared/layouts/ClientLayout";
import VendorLayout from "./components/shared/layouts/VendorLayout";
import AdminLayout from './components/shared/layouts/AdminLayout';


// Client Pages
import ClientHome from "./pages/client/ClientHome";
import ClientBrowse from "./pages/client/ClientBrowse";
import BrowseVendors from "./pages/client/BrowseVendors";
import SearchResults from "./pages/client/SearchResults";
import ClientVendorServices from "./pages/client/ClientVendorServices";
import ClientVendorProfile from "./pages/client/ClientVendorProfile";
import ClientBooking from "./pages/client/ClientBooking";
import ClientBookingSuccess from "./pages/client/ClientBookingSuccess";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientBookings from "./pages/client/ClientBookings";
import ClientBookingDetails from "./pages/client/ClientBookingDetails";
import ClientMessages from "./pages/client/ClientMessages";
import ClientFavorites from "./pages/client/ClientFavorites";
import ClientNotifications from "./pages/client/ClientNotifications";
import ClientReviews from "./pages/client/ClientReviews";
import ClientBilling from "./pages/client/ClientBilling";
import ClientTransactions from "./pages/client/ClientTransactions";
import ClientHelpSupport from "./pages/client/ClientHelpSupport";
import ClientSettings from "./pages/client/ClientSettings";
import TermsOfService from "./pages/client/TermsOfService";
import PrivacyPolicy from "./pages/client/PrivacyPolicy";
import HelpCenter from "./pages/client/HelpCenter";
import ContactUs from "./pages/client/ContactUs";
import Safety from "./pages/client/Safety";
import ReportProblem from "./pages/client/ReportProblem";
import VendorSuccessStories from "./pages/client/VendorSuccessStories";
import VendorSupport from "./pages/client/VendorSupport";
import CategoryServices from "./pages/client/CategoryServices";
import About from "./pages/client/About";
import HowItWorks from "./pages/client/HowItWorks";
import Careers from "./pages/client/Careers";
import Press from "./pages/client/Press";
import Blog from "./pages/client/Blog";
import BlogPostDetail from "./pages/client/BlogPostDetail";

// Vendor Pages
import VendorDashboard from "./pages/vendor/VendorDashboard";
import VendorProfile from "./pages/vendor/VendorProfile";
import VendorSettings from "./pages/vendor/VendorSettings";
import VendorAccount from "./pages/vendor/VendorAccount";
import VendorSecurity from "./pages/vendor/VendorSecurity";
import VendorPreferences from "./pages/vendor/VendorPreferences";
import VendorVerification from "./pages/vendor/VendorVerification";
import VendorBookings from "./pages/vendor/VendorBookings";
import VendorBookingDetails from "./pages/vendor/VendorBookingDetails";
import VendorCalendar from "./pages/vendor/VendorCalendar";
import VendorBookingRequests from "./pages/vendor/VendorBookingRequests";
import VendorBookingHistory from "./pages/vendor/VendorBookingHistory";
import VendorPortfolio from "./pages/vendor/VendorPortfolio";
import VendorGallery from "./pages/vendor/VendorGallery";
import VendorAnalytics from "./pages/vendor/VendorAnalytics";
import VendorEarnings from "./pages/vendor/VendorEarnings";
import VendorPayments from "./pages/vendor/VendorPayments";
import VendorPayoutDisbursements from "./pages/vendor/VendorPayoutDisbursements";
import VendorDiscountCodes from "./pages/vendor/VendorDiscountCodes";
import VendorInvoices from "./pages/vendor/VendorInvoices";
import VendorInvoiceCreate from "./pages/vendor/VendorInvoiceCreate";
import VendorInvoiceEdit from "./pages/vendor/VendorInvoiceEdit";
import VendorInvoiceDetail from "./pages/vendor/VendorInvoiceDetail";
import VendorContracts from "./pages/vendor/VendorContracts";
import VendorContractCreate from "./pages/vendor/VendorContractCreate";
import VendorContractDetail from "./pages/vendor/VendorContractDetail";
import ContractSign from "./pages/ContractSign";
import VendorTaxReports from "./pages/vendor/VendorTaxReports";
import VendorPerformance from "./pages/vendor/VendorPerformance";
import VendorReports from "./pages/vendor/VendorReports";
import VendorInsights from "./pages/vendor/VendorInsights";
import VendorSubscription from "./pages/vendor/VendorSubscription";
import VendorPlanFeatures from "./pages/vendor/VendorPlanFeatures";
import VendorCustomBranding from "./pages/vendor/VendorCustomBranding";
import VendorMultiLocation from "./pages/vendor/VendorMultiLocation";
import VendorAdvancedAnalytics from "./pages/vendor/VendorAdvancedAnalytics";
import VendorAdvancedBookings from "./pages/vendor/VendorAdvancedBookings";
import VendorPrioritySupport from "./pages/vendor/VendorPrioritySupport";
import VendorMessages from "./pages/vendor/VendorMessages";
import VendorNotifications from "./pages/vendor/VendorNotifications";
import VendorConsultations from "./pages/vendor/VendorConsultations";
import VendorPrivateBookings from "./pages/vendor/VendorPrivateBookings";
import VendorReviews from "./pages/vendor/VendorReviews";
import { VendorServices, VendorServicePackages } from "./pages/vendor/services";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminBookingDetails from "./pages/admin/AdminBookingDetails";
import AdminCalendar from "./pages/admin/AdminCalendar";
import AdminVendors from "./pages/admin/AdminVendors";
import AdminVendorDetails from "./pages/admin/AdminVendorDetails";
import AdminContent from "./pages/admin/AdminContent";
import AdminServiceCategories from "./pages/admin/AdminServiceCategories";
import AdminCountries from "./pages/admin/AdminCountries";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminVendorPayouts from "./pages/admin/AdminVendorPayouts";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminRefunds from "./pages/admin/AdminRefunds";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminAdvancedAnalytics from "./pages/admin/AdminAdvancedAnalytics";
import AdminReports from "./pages/admin/AdminReports";
import AdminServicesReport from "./pages/admin/AdminServicesReport";
import AdminRevenue from "./pages/admin/AdminRevenue";
import AdminPerformance from "./pages/admin/AdminPerformance";
import AdminFailedOperations from "./pages/admin/AdminFailedOperations";
import AdminComplianceAudit from "./pages/admin/AdminComplianceAudit";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminContactMessages from "./pages/admin/AdminContactMessages";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminSystemMessages from "./pages/admin/AdminSystemMessages";
import AdminEmailTemplates from "./pages/admin/AdminEmailTemplates";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import AdminSecurity from "./pages/admin/AdminSecurity";
import AdminSystemLogs from "./pages/admin/AdminSystemLogs";
import AdminSupportTickets from "./pages/admin/AdminSupportTickets";
import AdminSupportTicketDetail from "./pages/admin/AdminSupportTicketDetail";
import AdminFAQ from "./pages/admin/AdminFAQ";
import AdminCompliance from "./pages/admin/AdminCompliance";
import AdminTermsPrivacy from "./pages/admin/AdminTermsPrivacy";
import AdminDataProtection from "./pages/admin/AdminDataProtection";
import AdminAuditTrail from "./pages/admin/AdminAuditTrail";
import AdminVendorSpotlight from "./pages/admin/AdminVendorSpotlight";
import AdminVendorSuccessStories from "./pages/admin/AdminVendorSuccessStories";
import AdminBlogPosts from "./pages/admin/AdminBlogPosts";
import AdminPressReleases from "./pages/admin/AdminPressReleases";
import AdminJobPostings from "./pages/admin/AdminJobPostings";
import AdminTermsOfService from "./pages/admin/AdminTermsOfService";
import AdminPrivacyPolicy from "./pages/admin/AdminPrivacyPolicy";
import { AdminVendorServices } from "./pages/admin/services";
import { AdminClients, AdminClientDetail } from "./pages/admin/clients";
import { AdminSubscriptions, AdminSubscriptionPayments } from "./pages/admin/subscriptions";

// Auth Pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";

// Protected Route Component
import ProtectedRoute from "./components/shared/ProtectedRoute";
import PublicRoute from "./components/shared/PublicRoute";
import BookingSuccessRoute from "./components/shared/BookingSuccessRoute";
// import AdminLayout from "./components/shared/layouts/AdminLayout";
import { InternetStatus } from "./components/shared";
import { useAuth } from "./contexts/AuthContext";
import { useSubscription } from "./contexts/SubscriptionContext";
import SubscriptionModal from "./components/shared/SubscriptionModal";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error?.response?.status === 429 || error?.isRateLimited) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

// Toast Container Component
const ToastWrapper = () => {
  const { toasts, removeToast } = useToastContext();
  return (
    <ToastContainer
      toasts={toasts}
      onRemove={removeToast}
      position="top-right"
    />
  );
};

// App Content Component (needs to be inside AuthProvider)
const AppContent = () => {
  const { logout, user } = useAuth();
  const { showSubscriptionModalState, hideSubscriptionModal, modalVariant } = useSubscription();

  // Listen for session expiration events
  const hasHandledSessionExpired = React.useRef(false);
  
  React.useEffect(() => {
    const handleSessionExpired = async (event) => {
      // Prevent multiple calls
      if (hasHandledSessionExpired.current) {
        return;
      }
      
      hasHandledSessionExpired.current = true;
      
      await logout();
      // Show a toast notification
      window.dispatchEvent(
        new CustomEvent("showToast", {
          detail: {
            type: "error",
            message: event.detail.message,
            duration: 5000,
          },
        })
      );
      
      // Reset after a delay
      setTimeout(() => {
        hasHandledSessionExpired.current = false;
      }, 2000);
    };

    window.addEventListener("sessionExpired", handleSessionExpired);
    return () => {
      window.removeEventListener("sessionExpired", handleSessionExpired);
      hasHandledSessionExpired.current = false;
    };
  }, [logout]);

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <ToastWrapper />
        <InternetStatus position="bottom-right" />
        {/* Only show subscription modal for vendors */}
        {user?.user_type === 'vendor' && (
          <SubscriptionModal
            isOpen={showSubscriptionModalState}
            onClose={hideSubscriptionModal}
            variant={modalVariant}
          />
        )}
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />
          <Route path="/reset-password" element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } />

          {/* Public contract signing (token link — no login) */}
          <Route path="/sign/contract/:token" element={<ContractSign />} />

          {/* Client Routes */}
          <Route path="/" element={<ClientLayout />}>
            <Route index element={<ClientHome />} />
            <Route path="browse" element={<ClientBrowse />} />
            <Route path="browse-vendors" element={<BrowseVendors />} />
            <Route path="search" element={<SearchResults />} />
            <Route path="category/:category" element={<CategoryServices />} />
            <Route path="services/:id" element={<ClientVendorServices />} />
            <Route path="vendor/:id" element={<ClientVendorProfile />} />
            <Route path="booking/:id" element={<ClientBooking />} />
            <Route path="booking-success" element={
              <BookingSuccessRoute>
                <ClientBookingSuccess />
              </BookingSuccessRoute>
            } />
            <Route
              path="dashboard"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="bookings"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="bookings/:id"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientBookingDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="messages"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="favorites"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientFavorites />
                </ProtectedRoute>
              }
            />
            <Route
              path="notifications"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="reviews"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientReviews />
                </ProtectedRoute>
              }
            />
            <Route
              path="billing"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientBilling />
                </ProtectedRoute>
              }
            />
            <Route
              path="transactions"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientTransactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientSettings />
                </ProtectedRoute>
              }
            />
            <Route path="about" element={<About />} />
            <Route path="how-it-works" element={<HowItWorks />} />
            <Route path="careers" element={<Careers />} />
            <Route path="press" element={<Press />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogPostDetail />} />
            <Route path="terms" element={<TermsOfService />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="help" element={<HelpCenter />} />
            <Route path="contact" element={<ContactUs />} />
            <Route path="safety" element={<Safety />} />
            <Route path="report" element={<ReportProblem />} />
            <Route path="vendor/success" element={<VendorSuccessStories />} />
          </Route>

          {/* Standalone Vendor Subscription Route */}
          <Route
            path="/vendor/subscription"
            element={
              <ProtectedRoute allowedRoles={["vendor"]}>
                <VendorSubscription />
              </ProtectedRoute>
            }
          />

          {/* Vendor Routes */}
          <Route path="/vendor" element={<VendorLayout />}>
            <Route
              index
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="profile"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="account"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="security"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorSecurity />
                </ProtectedRoute>
              }
            />
            <Route
              path="preferences"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorPreferences />
                </ProtectedRoute>
              }
            />
            <Route
              path="bookings"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="bookings/:id"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorBookingDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="advanced-bookings"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorAdvancedBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="private-bookings"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorPrivateBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="consultations"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorConsultations />
                </ProtectedRoute>
              }
            />
            <Route
              path="calendar"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="requests"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorBookingRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="history"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorBookingHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="portfolio"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorPortfolio />
                </ProtectedRoute>
              }
            />
            <Route
              path="gallery"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorGallery />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="advanced-analytics"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorAdvancedAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="performance"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorPerformance />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="insights"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorInsights />
                </ProtectedRoute>
              }
            />
            <Route
              path="earnings"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorEarnings />
                </ProtectedRoute>
              }
            />
            <Route
              path="payments"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="payout-disbursements"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorPayoutDisbursements />
                </ProtectedRoute>
              }
            />
            <Route
              path="discount-codes"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorDiscountCodes />
                </ProtectedRoute>
              }
            />
            <Route
              path="invoices"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorInvoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="invoices/create"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorInvoiceCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="invoices/:id/edit"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorInvoiceEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="invoices/:id"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorInvoiceDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="contracts"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorContracts />
                </ProtectedRoute>
              }
            />
            <Route
              path="contracts/create"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorContractCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="contracts/:id/edit"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorContractCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="contracts/:id"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorContractDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="tax-reports"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorTaxReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="services"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorServices />
                </ProtectedRoute>
              }
            />
            <Route
              path="packages"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorServicePackages />
                </ProtectedRoute>
              }
            />
            <Route
              path="messages"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="notifications"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="reviews"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorReviews />
                </ProtectedRoute>
              }
            />
            <Route
              path="support"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorSupport />
                </ProtectedRoute>
              }
            />
            <Route
              path="plan"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorPlanFeatures />
                </ProtectedRoute>
              }
            />
            <Route
              path="branding"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorCustomBranding />
                </ProtectedRoute>
              }
            />
            <Route
              path="locations"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorMultiLocation />
                </ProtectedRoute>
              }
            />
            <Route
              path="support-tickets"
              element={
                <ProtectedRoute allowedRoles={["vendor"]}>
                  <VendorPrioritySupport />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Standalone Vendor Verification Route */}
          <Route
            path="/vendor/verification"
            element={
              <ProtectedRoute allowedRoles={["vendor"]}>
                <VendorVerification />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route
              index
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
              <Route
                path="bookings"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bookings/:id"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminBookingDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bookings/calendar"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminCalendar />
                  </ProtectedRoute>
                }
              />
            <Route
              path="clients"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminClients />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/:id"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminClientDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="vendors"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminVendors />
                </ProtectedRoute>
              }
            />
            <Route
              path="vendors/:id"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminVendorDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="content"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminContent />
                </ProtectedRoute>
              }
            />
            <Route
              path="vendor-spotlight"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminVendorSpotlight />
                </ProtectedRoute>
              }
            />
            <Route
              path="vendor-success-stories"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminVendorSuccessStories />
                </ProtectedRoute>
              }
            />
            <Route
              path="blog-posts"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminBlogPosts />
                </ProtectedRoute>
              }
            />
            <Route
              path="press-releases"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminPressReleases />
                </ProtectedRoute>
              }
            />
            <Route
              path="job-postings"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminJobPostings />
                </ProtectedRoute>
              }
            />
            <Route
              path="terms-of-service"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminTermsOfService />
                </ProtectedRoute>
              }
            />
            <Route
              path="privacy-policy"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminPrivacyPolicy />
                </ProtectedRoute>
              }
            />
            <Route
              path="service-categories"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminServiceCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="countries"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminCountries />
                </ProtectedRoute>
              }
            />
            <Route
              path="services"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminVendorServices />
                </ProtectedRoute>
              }
            />
            <Route
              path="subscriptions"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminSubscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="subscription-payments"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminSubscriptionPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="vendor-payouts"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminVendorPayouts />
                </ProtectedRoute>
              }
            />
            <Route
              path="payments"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="transactions"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminTransactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="refunds"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminRefunds />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="advanced-analytics"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminAdvancedAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports/services"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminServicesReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="revenue"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminRevenue />
                </ProtectedRoute>
              }
            />
            <Route
              path="performance"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminPerformance />
                </ProtectedRoute>
              }
            />
            <Route
              path="failed-operations"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminFailedOperations />
                </ProtectedRoute>
              }
            />
            <Route
              path="compliance-audit"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminComplianceAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="messages"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="contact-messages"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminContactMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="notifications"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="system-messages"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminSystemMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="email-templates"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminEmailTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="announcements"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminAnnouncements />
                </ProtectedRoute>
              }
            />
            <Route
              path="security"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminSecurity />
                </ProtectedRoute>
              }
            />
            <Route
              path="system-logs"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminSystemLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="support-tickets"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminSupportTickets />
                </ProtectedRoute>
              }
            />
            <Route
              path="support-tickets/:id"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminSupportTicketDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="faqs"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminFAQ />
                </ProtectedRoute>
              }
            />
            <Route
              path="compliance"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminCompliance />
                </ProtectedRoute>
              }
            />
            <Route
              path="terms-privacy"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminTermsPrivacy />
                </ProtectedRoute>
              }
            />
            <Route
              path="data-protection"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDataProtection />
                </ProtectedRoute>
              }
            />
            <Route
              path="audit-trail"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminAuditTrail />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <LayoutProvider>
            <SidebarProvider>
              <ToastProvider>
                <AppContent />
              </ToastProvider>
            </SidebarProvider>
          </LayoutProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
